import React, { useEffect, useState } from 'react';
import { liveAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Radio, Copy, CheckCircle2, AlertTriangle, Save, Loader2, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_LABELS = {
  'member.approved': 'Miembro aprobado',
  'member.rejected': 'Miembro rechazado',
  'event.created': 'Evento creado',
  'post.created': 'Noticia publicada',
  'announcement.created': 'Anuncio publicado',
  'contribution.paid': 'Aporte pagado',
  'contribution.updated': 'Aporte corregido',
  'report.created': 'Reporte de contenido',
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'hace unos segundos';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.round(hours / 24)} días`;
};

export const LiveDataCard = () => {
  const { selectedChurchId } = useAuth();
  const [live, setLive] = useState(null);
  const [diag, setDiag] = useState(null);
  const [secret, setSecret] = useState('');
  const [savingSecret, setSavingSecret] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setRefreshing(true);
    Promise.all([
      liveAPI.webhooks(selectedChurchId).then(res => setLive(res.data)).catch(() => setLive(null)),
      liveAPI.diagnostics().then(res => setDiag(res.data)).catch(() => setDiag(null)),
    ]).finally(() => setRefreshing(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedChurchId]);

  // Partner-wide integration: configurable only from the congregation that owns it.
  if (!live || live.is_integration_church === false) return null;

  const registered = !!live?.registered_in_hub;
  const otherHooks = (live?.data || []).filter(w => w.url !== live?.receiver_url);

  const requestText = live ? [
    'Solicitud: registrar el webhook de la Suite Ministerial',
    '',
    `URL del receptor: ${live.receiver_url}`,
    'Firma: HMAC-SHA256 del cuerpo crudo en la cabecera X-TuIglesia-Signature (formato sha256=<hmac>)',
    `Eventos a suscribir: ${(live.supported_events || []).join(', ')}`,
    live.data?.[0]?.partner_id ? `Partner ID: ${live.data[0].partner_id}` : '',
    '',
    'Pasos en el panel del Hub:',
    '1. Webhooks → Nuevo webhook',
    '2. Pegar la URL del receptor y seleccionar los eventos de arriba',
    '3. Guardar y copiar el signing_secret que se muestra una sola vez',
    '4. Pegar ese signing_secret en la Suite: Configuración → Datos en vivo → Paso 2',
  ].filter(Boolean).join('\n') : '';

  const copy = (text, message) => {
    navigator.clipboard.writeText(text);
    toast.success(message);
  };

  const saveSecret = async () => {
    setSavingSecret(true);
    try {
      await liveAPI.setSecret(secret.trim());
      toast.success('Signing secret guardado');
      setSecret('');
      setTestResult(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar el secreto');
    } finally {
      setSavingSecret(false);
    }
  };

  const testSecret = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await liveAPI.testSecret();
      setTestResult({ ok: true, message: res.data?.detail || 'Conexión verificada' });
      toast.success('Receptor funcionando');
    } catch (err) {
      setTestResult({ ok: false, message: err?.response?.data?.detail || 'No se pudo verificar' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card data-testid="settings-live-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radio className="w-5 h-5" /> Datos en vivo
          <span className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${registered && live?.receiver_ready ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
            {registered && live?.receiver_ready ? 'Activo' : 'Pendiente'}
          </span>
          <Button variant="ghost" size="sm" className="ml-auto h-8" onClick={load} disabled={refreshing} data-testid="settings-live-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Cuando el Hub de Tu Iglesia App envía los eventos a esta URL, los miembros aprobados,
          los aportes pagados y los reportes nuevos aparecen en la Suite sin recargar la página.
        </p>

        {live && !registered && (
          <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 space-y-1" data-testid="settings-webhook-not-registered">
            <p className="text-sm font-medium text-yellow-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Tu receptor todavía no está registrado en el Hub
            </p>
            <p className="text-xs text-yellow-800/90">
              Hasta que el administrador del Hub registre esta URL, la Suite seguirá funcionando
              con recargas manuales: no recibirá eventos en vivo.
              {otherHooks.length > 0 && ' Hoy hay otro webhook registrado que apunta a una URL distinta.'}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Paso 1 · Registra esta URL en el panel del Hub
          </Label>
          <div className="flex gap-2">
            <Input readOnly value={live?.receiver_url || ''} className="font-mono text-xs" data-testid="settings-webhook-url" />
            <Button variant="outline" size="sm" onClick={() => copy(live?.receiver_url || '', 'URL copiada')} data-testid="settings-webhook-copy">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Eventos a registrar: {(live?.supported_events || []).join(', ')}
          </p>
          {registered ? (
            <p className="text-xs text-green-700 flex items-center gap-1" data-testid="settings-webhook-registered">
              <CheckCircle2 className="w-3 h-3" /> Esta URL ya está registrada en el Hub
            </p>
          ) : (
            <Button variant="outline" size="sm" className="mt-1" onClick={() => copy(requestText, 'Instrucciones copiadas')} data-testid="settings-webhook-copy-request">
              <Copy className="w-4 h-4 mr-1" /> Copiar instrucciones de registro
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Paso 2 · Pega el signing_secret que el Hub muestra una sola vez
          </Label>
          <div className="flex gap-2">
            <Input
              type="password"
              value={secret}
              onChange={e => setSecret(e.target.value)}
              placeholder={live?.secret_hint ? `Guardado (${live.secret_hint})` : 'whsec_...'}
              className="font-mono text-xs"
              data-testid="settings-webhook-secret-input"
            />
            <Button
              className="bg-[#056A84] hover:bg-[#045A70] text-white shrink-0"
              onClick={saveSecret}
              disabled={savingSecret || secret.trim().length < 8}
              data-testid="settings-webhook-secret-save"
            >
              {savingSecret ? <Loader2 className="w-4 h-4 spinner" /> : <Save className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Se guarda cifrado del lado del servidor; nadie más en la Suite puede leerlo.
            El evento de la prueba de conexión no se contabiliza en el diagnóstico.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={testSecret} disabled={testing} data-testid="settings-webhook-test">
            {testing ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Radio className="w-4 h-4 mr-1" />}
            Probar conexión
          </Button>
          {testResult && (
            <span className={`text-xs ${testResult.ok ? 'text-green-700' : 'text-destructive'}`} data-testid="settings-webhook-test-result">
              {testResult.message}
            </span>
          )}
        </div>

        <div className="space-y-2 rounded-lg border bg-muted/20 p-3" data-testid="settings-webhook-diagnostics">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Activity className="w-3.5 h-3.5" /> Diagnóstico del receptor
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-sm font-semibold text-[#056A84]" data-testid="diag-events-24h">{diag?.events_24h ?? 0}</p>
              <p className="text-[11px] text-muted-foreground">eventos (24 h)</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#056A84]" data-testid="diag-total-events">{diag?.total_events ?? 0}</p>
              <p className="text-[11px] text-muted-foreground">eventos recibidos</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#056A84]" data-testid="diag-last-event">
                {diag?.last_event_at ? relativeTime(diag.last_event_at) : 'Sin entregas'}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {diag?.last_event ? EVENT_LABELS[diag.last_event] || diag.last_event : 'última entrega'}
              </p>
            </div>
          </div>
          {diag?.by_event_24h?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {diag.by_event_24h.map(item => (
                <span key={item.event} className="px-2 py-0.5 rounded-full border bg-card text-[10px]">
                  {EVENT_LABELS[item.event] || item.event}: {item.count}
                </span>
              ))}
            </div>
          )}
        </div>

        {live?.data?.length > 0 && (
          <div className="space-y-2" data-testid="settings-webhook-list">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Webhooks registrados en el Hub</Label>
            {live.data.map(w => {
              const mine = w.url === live.receiver_url;
              return (
                <div key={w.id || w.url} className="flex items-center justify-between gap-2 p-2 rounded-lg border text-xs" data-testid={`webhook-row-${w.id || 'url'}`}>
                  <span className="font-mono truncate">{w.url}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-muted-foreground">{(w.events || []).length} eventos</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${mine ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      {mine ? 'Esta Suite' : 'Otra URL'}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
